import { Clock, FileQuestion, Minus, Plus } from 'lucide-react';
import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import { DIFFICULTY_LEVELS } from '../../../types/wizard';
import type { DifficultyLevel } from '../../../types/wizard';
import { cn } from '../../../lib/utils';

interface SpinnerFieldProps {
  label: string;
  value: number;
  unit: string;
  step: number;
  min: number;
  onChange: (v: number) => void;
  icon: React.ComponentType<{ className?: string }>;
  hint?: string;
}

function SpinnerField({
  label,
  value,
  unit,
  step,
  min,
  onChange,
  icon: Icon,
  hint,
}: Readonly<SpinnerFieldProps>) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-300">
        <Icon className="h-4 w-4 text-brand-500" />
        {label}
      </label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - step))}
          className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 transition-all duration-150"
        >
          <Minus className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 rounded-xl border-2 border-slate-700 bg-slate-700 px-4 py-2 min-w-[130px] justify-center focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-500/20 transition-all">
          <input
            type="number"
            value={value}
            min={min}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) onChange(n);
            }}
            className="w-16 text-center text-lg font-bold text-white focus:outline-none bg-transparent"
          />
          <span className="text-sm text-slate-400 font-medium">{unit}</span>
        </div>
        <button
          type="button"
          onClick={() => onChange(value + step)}
          className="flex h-10 w-10 items-center justify-center rounded-xl border-2 border-slate-700 bg-slate-700 text-slate-300 hover:border-violet-500 hover:bg-violet-500/10 hover:text-violet-400 transition-all duration-150"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

const DIFFICULTY_STYLES: Record<string, { selected: string; badge: string }> = {
  green: {
    selected: 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-400/30 shadow-md',
    badge: 'text-emerald-700',
  },
  yellow: {
    selected: 'border-amber-400 bg-amber-50 ring-2 ring-amber-400/30 shadow-md',
    badge: 'text-amber-700',
  },
  red: {
    selected: 'border-rose-400 bg-rose-50 ring-2 ring-rose-400/30 shadow-md',
    badge: 'text-rose-700',
  },
  indigo: {
    selected: 'border-brand-400 bg-brand-50 ring-2 ring-brand-400/30 shadow-md',
    badge: 'text-brand-700',
  },
};

export function Step5Questions() {
  const { state, setNumberOfQuestions, setDuration, setDifficulty } = useWizard();

  const isWritten = state.selectedExamType === 'written';

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-white">Questions &amp; Duration</h2>
        <p className="text-sm text-slate-500 mt-1">
          Set the number of questions, time allowed, and difficulty level.
        </p>
      </div>

      <div className="space-y-6">
        <SpinnerField
          label="Number of Questions"
          value={state.numberOfQuestions}
          unit="questions"
          step={1}
          min={1}
          onChange={setNumberOfQuestions}
          icon={FileQuestion}
          hint={
            isWritten
              ? 'For written exams each question has ক, খ, গ, ঘ parts.'
              : 'Each MCQ question carries 1 mark.'
          }
        />

        <SpinnerField
          label="Duration"
          value={state.duration}
          unit="min"
          step={15}
          min={15}
          onChange={setDuration}
          icon={Clock}
          hint="Use the +/− buttons or type a value directly."
        />

        {/* Difficulty selector */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-300">Difficulty Level</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {DIFFICULTY_LEVELS.map((level) => {
              const isSelected = state.difficulty === level.id;
              const styles = DIFFICULTY_STYLES[level.color];
              return (
                <button
                  key={level.id}
                  type="button"
                  onClick={() => setDifficulty(level.id as DifficultyLevel)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-2xl border-2 px-3 py-4 text-center transition-all duration-200',
                    isSelected
                      ? styles.selected
                      : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:-translate-y-0.5 hover:shadow-card-hover'
                  )}
                >
                  <span
                    className={cn('text-sm font-bold', isSelected ? styles.badge : 'text-white')}
                  >
                    {level.label}
                  </span>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isSelected ? styles.badge : 'text-slate-500'
                    )}
                  >
                    {level.banglaLabel}
                  </span>
                  <span
                    className={cn(
                      'text-[10px] mt-0.5',
                      isSelected ? styles.badge : 'text-slate-400'
                    )}
                  >
                    {level.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <NavigationButtons />
    </div>
  );
}
